import React from 'react';
import { ServiceCategory } from '@prisma/client';
import { FormikValues } from 'formik';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import {
  BusinessService,
  UpsertBusinessServiceInitValues,
} from '../../../types/businesses';
import useServiceForm from './useServiceForm';
import CustomizeBookingFormModal from './CustomizeBookingFormModal';
import ServiceCommissionsSection from './ServiceCommissionsSection';

interface Props {
  open: boolean;
  service: BusinessService | null;
  onClose: () => void;
  refetch: () => Promise<unknown>;
  refetchWaivers: () => Promise<unknown>;
}

function ServiceFormModal({
  open,
  service,
  onClose,
  refetch,
  refetchWaivers,
}: Props) {
  const [formData, setFormData] =
    React.useState<FormData<UpsertBusinessServiceInitValues> | null>(null);
  const [customizeOpen, setCustomizeOpen] = React.useState(false);
  const [serviceCategory, setServiceCategory] = React.useState<
    ServiceCategory | undefined
  >(undefined);
  const pendingIntakeQuestionsRef = React.useRef<
    { questionId: string; enabled: boolean }[]
  >([]);

  const { getServiceForm } = useServiceForm({
    refetch,
    refetchWaivers,
    setForm: form => {
      if (!form) onClose();
    },
    onCustomizeBookingForm: () => setCustomizeOpen(true),
    getPendingIntakeQuestions: () => pendingIntakeQuestionsRef.current,
  });

  const getServiceFormRef = React.useRef(getServiceForm);
  getServiceFormRef.current = getServiceForm;

  React.useEffect(() => {
    if (open) {
      pendingIntakeQuestionsRef.current = [];
      setFormData(getServiceFormRef.current(service ?? undefined));
    } else {
      setFormData(null);
      setCustomizeOpen(false);
      setServiceCategory(undefined);
    }
  }, [open, service]);

  const handleValuesChange = React.useCallback((values: FormikValues) => {
    setServiceCategory(values.serviceCategory || undefined);
  }, []);

  return (
    <>
      <FormSmallModal
        form={formData}
        open={open && !!formData}
        onClose={onClose}
        onValuesChange={handleValuesChange}>
        <ServiceCommissionsSection />
      </FormSmallModal>
      {customizeOpen && (
        <CustomizeBookingFormModal
          open
          service={service}
          serviceCategory={serviceCategory}
          pendingQuestions={pendingIntakeQuestionsRef.current}
          onClose={() => setCustomizeOpen(false)}
          onSaved={() => refetch()}
          onLocalSave={
            service === null
              ? questions => {
                  pendingIntakeQuestionsRef.current = questions;
                  setCustomizeOpen(false);
                }
              : undefined
          }
        />
      )}
    </>
  );
}

export default ServiceFormModal;
